import React from 'react'
import {GetCategoryProductsQuery} from '@data/__generated__/types.main'
import {useUserPreferences} from '@data/queries/preferences/userPreferences.main'
import getQueryObject from '@helpers/misc/getQueryObject'
import hasWebpSupport from '@helpers/misc/hasWebpSupport'
import {useEffect} from '@hooks/useEffect'
import useIsStore from '@hooks/useIsStore'
import usePagination from '@hooks/usePagination'
import useWebsiteId from '@hooks/useWebsiteId'
import {filterUnavailableProducts} from '@page-components/Order/Products/ProductsContent/Category'
import {NextRouter, useRouter} from 'next/router'
import {stringify} from 'querystring'

import useProductFiltersContext from '../ProductFilters/hooks/useProductFiltersContext'

import query from './getCategoryProductsQuery.main'
import NoProducts from './NoProducts'
import Pagination from './Pagination'
import ProductCard from './ProductCard'
import ProductModal from './ProductModal'

import styles from './styles.module.css'

const selectedPriceFilters = priceFilters =>
  priceFilters.reduce(
    (acc, priceFilter) =>
      priceFilter.value ? [...acc, {min: priceFilter.range.min, max: priceFilter.range.max}] : acc,
    []
  )
const getProductUrl = (
  router: NextRouter,
  categoryId: string,
  page?: number,
  filter?: string,
  productId?: string
) => {
  const query = getQueryObject(router)
  const path = `/pedir/category/${categoryId}`
  if (productId) {
    query.productId = productId
  } else {
    delete query.productId
  }
  if (page) query.page = String(page)
  if (filter) query.q = filter

  const url = `${path}?${stringify(query)}`

  return url
}

const LO_SALDES_WEBSITE_ID = 'GJpov9s9mgxoe8Ly9'

export default function Products({
  categoryId,
  filter,
  parent,
  order,
  shouldHidePrice,
  categoryName
}) {
  const websiteId = useWebsiteId()
  const isLoSaldes = websiteId === LO_SALDES_WEBSITE_ID
  const {productFilters, priceFilters} = useProductFiltersContext()
  const format = hasWebpSupport() ? 'webp' : 'jpeg'
  const isStore = useIsStore()
  const router = useRouter()
  const data = useUserPreferences()
  const {menuId, storeId, deliverAt} = data

  const queryPage = router.query.page
  let paramPage
  if (queryPage) {
    paramPage = Array.isArray(queryPage) ? queryPage[0] : queryPage
  }

  const {
    data: {
      category: {paginatedProducts}
    },
    page,
    setPage
  }: {
    data: GetCategoryProductsQuery
    page: number
    setPage: (page: number) => void
  } = usePagination({
    query,
    variables: {
      categoryId,
      menuId,
      format,
      filter,
      productFilters,
      priceFilters: selectedPriceFilters(priceFilters),
      storeId,
      deliverAt,
      ...(order && {sorting: {sortBy: order.type, direction: order.order}})
    },
    // a single product can be created up to 7 times and we need to try to prevent more than 1 page
    initialLimit: isLoSaldes ? 200 : 12
  })

  const {hasNextPage, hasPreviousPage, items: products, totalCount, totalPages} = paginatedProducts

  const openCloseModal = productId => {
    router.push(getProductUrl(router, categoryId, page, filter, productId), undefined, {
      scroll: false
    })
  }

  useEffect(() => {
    setPage(1)
  }, [categoryId, order, filter])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [categoryId, filter, page])

  useEffect(() => {
    if (paramPage) {
      setPage(parseInt(paramPage, 10) || 1)
    }
  }, [])

  const filterUnavailableProductsFn = filterUnavailableProducts(false) //TODO: this function must be deleted after hideNotAvailableProducts is separated into hideOutOfStockProducts and hideNotAvailableProducts in admin

  return totalCount > 0 ? (
    <>
      <div className={styles.flexWrap}>
        {products
          // This should honestly be done with aggregation cursors on the backend,
          // but after estimating the time that it would take and considering it's just a single website with weird flows. I did it here as it's already done in the normal website
          .filter(product => {
            if (!isLoSaldes) return true
            return filterUnavailableProductsFn(product) //TODO: this function must be deleted after hideNotAvailableProducts is separated into hideOutOfStockProducts and hideNotAvailableProducts in admin
          })
          .map(product => (
            <div key={product._id} className={styles.product}>
              <ProductCard
                channels={product.menu.channels}
                product={product}
                menuId={menuId}
                href={getProductUrl(router, categoryId, page, filter, product._id)}
                isStore={isStore}
                hidePrice={shouldHidePrice}
                categoryId={categoryId}
                categoryName={categoryName}
              />
            </div>
          ))}
      </div>
      {totalPages !== 1 && (
        <Pagination
          page={page}
          setPage={setPage}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          totalPages={totalPages}
        />
      )}
      <ProductModal paginatedProducts={paginatedProducts} onClose={openCloseModal} />
    </>
  ) : (
    <NoProducts parent={parent} />
  )
}
